// ══════════════════════════════════════════════════════════════════════════════════════════════
// LES CHAMPS DE FUSION — déclarés par le CONSOMMATEUR, jamais connus d'ici.
//
// ⚠️ 🔴 CE PAQUET NE CONNAÎT PAS `{prénom}`. Le marketing a ses champs (le porteur d'une carte), la
// facture les siens (un numéro de pièce, une échéance), la Console les siens (un magasin, un lien de
// dossier). Les écrire ici ferait connaître au gabarit de la facture les champs d'une campagne, et
// réciproquement — et la première liste ajoutée par confort serait celle qu'on n'ose plus enlever.
//
// ⚠️ 🔴 UN JETON NE SE TRADUIT JAMAIS. Son NOM est sa clé : le serveur le reconnaît à `{prénom}`, et
// `{first name}` ne serait remplacé par rien — le destinataire lirait le jeton (Forme CQ, règle déjà
// écrite au dépôt). Les champs s'affichent pour être COPIÉS, pas pour être lus.
//
// ⚠️ 🔴 ET UNE VALEUR MANQUANTE NE LAISSE PAS LE JETON. « Bonjour {prénom}, » parti tel quel est le
// défaut mesuré en production ; chaque champ porte donc une valeur de REPLI obligatoire, et le rendu
// ne peut pas produire d'accolade orpheline.
// ══════════════════════════════════════════════════════════════════════════════════════════════

/** Un champ de fusion, tel qu'un consommateur le déclare. */
export interface MergeField {
  /**
   * Le nom DANS le gabarit, sans accolades — ex. `prénom`.
   * ⚠️ C'est une CLÉ, pas un libellé : il ne se traduit pas et ne se renomme pas sans migrer les
   * gabarits déjà écrits qui le citent.
   */
  name: string;
  /** Ce que le champ vaut, dit à l'humain qui compose — affiché à côté de la pastille. */
  description: string;
  /**
   * 🔴 CE QUI PART QUAND LA VALEUR MANQUE. Obligatoire, et c'est tout l'intérêt : « Bonjour, » sans
   * prénom se lit ; « Bonjour {prénom}, » trahit le produit devant le client.
   * ⚠️ Une chaîne VIDE est une réponse valable (le fragment disparaît) ; ce qui est interdit est de ne
   * pas avoir décidé.
   */
  fallback: string;
  /**
   * 🔴 LE GABARIT DOIT-IL LE CITER ?
   *
   * ⚠️ 🔴 CERTAINS MESSAGES NE VEULENT RIEN DIRE SANS UN CHAMP. Une invitation à remplir un dossier
   * qui aurait perdu son LIEN part parfaitement, s’affiche parfaitement, et le destinataire ne peut
   * RIEN en faire — il n’a même pas de quoi comprendre ce qu’on attend de lui (§23.0). Le repli ne
   * sauve pas ce cas-là : il remplace une valeur absente, pas un jeton qu’on a oublié d’écrire.
   *
   * ⚠️ LE REFUS A LIEU À L’ÉCRITURE, quand un humain peut corriger — jamais à l’envoi.
   */
  required?: boolean;
}

/** Le motif d'un refus — nommé, pour que l'écran puisse le dire. */
export class TemplateRejected extends Error {
  constructor(
    readonly code: 'unknown_field' | 'invalid_field' | 'empty_subject' | 'empty_body' | 'missing_field' | 'invalid_button',
    message: string,
  ) {
    super(message);
    this.name = 'TemplateRejected';
  }
}

/**
 * Les noms de champ ACCEPTABLES.
   *
 * ⚠️ 🔴 LA CLASSE DE CARACTÈRES EST FERMÉE, ET ELLE DÉCIDE DU MOTIF DE RECHERCHE. Un nom qui pourrait
 * contenir `}`, `<` ou un espace rendrait le repérage des jetons ambigu — et un nom qui pourrait
 * contenir un point-virgule permettrait d'écrire `{a;b}` là où le HTML attend autre chose. Les lettres
 * accentuées sont ADMISES parce que le français EST la langue de ces clés (`prénom`, `palier`).
 */
const NOM_VALIDE = /^[\p{L}\p{N}_][\p{L}\p{N}_ -]{0,39}$/u;

/** Le motif qui repère un jeton dans un corps — la MÊME classe que ci-dessus, dérivée d'elle. */
export const TOKEN_PATTERN = /\{([\p{L}\p{N}_][\p{L}\p{N}_ -]{0,39})\}/gu;

/**
 * Vérifie qu'une déclaration de champs tient debout.
 * ⚠️ REFUSE AU LIEU DE FILTRER : un champ mal nommé qu'on écarterait en silence donnerait un gabarit
 * qui cite un jeton « inconnu » pour une raison que personne ne pourrait voir.
 */
export function assertFields(fields: readonly MergeField[]): void {
  const vus = new Set<string>();
  for (const f of fields) {
    if (!NOM_VALIDE.test(f.name)) {
      throw new TemplateRejected('invalid_field', `Nom de champ invalide : « ${f.name} ».`);
    }
    if (vus.has(f.name)) {
      throw new TemplateRejected('invalid_field', `Champ déclaré deux fois : « ${f.name} ».`);
    }
    vus.add(f.name);
  }
}

/**
 * Les jetons CITÉS par un texte, dans l'ordre et sans doublon.
 * ⚠️ Il lit le texte BRUT, accolades comprises : c'est le même repérage que la fusion, donc les deux
 * ne peuvent pas diverger sur ce qui est un jeton.
 */
export function tokensOf(texte: string): string[] {
  const vus: string[] = [];
  for (const m of texte.matchAll(TOKEN_PATTERN)) {
    const nom = m[1]!;
    if (!vus.includes(nom)) vus.push(nom);
  }
  return vus;
}
